import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionItem from '../../models/SessionItem';
import { SessionItemEditRequest } from '../../models/SessionItemEditRequest';
import { ItemEditHistEntry } from '../../models/ItemEditHistEntry';
import { editItem, getItemHistory } from '../../service/SessionService';
import { convertStrDateUTCToStrDateGB } from '../../utils/Utils';
import { useAuthContext } from '../../contexts/AuthContext';
import { Brand } from '../../models/Brand';

type Props = {
  sessionId: string;
  item: SessionItem;
  onClose: () => void;
  onSaved: (updatedItem: SessionItem) => void;
  /** Which tab to show when the modal opens. Defaults to 'edit' if canEdit, otherwise 'history'. */
  initialTab?: 'edit' | 'history';
  /** When false, the Edit tab is hidden (e.g. session is still open). */
  canEdit?: boolean;
};

const ReadOnlyField: React.FC<{ label: string; value?: string | string[] | null }> = ({ label, value }) => {
  const display = Array.isArray(value) ? value.join(', ') || '—' : value || '—';
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <p className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">{display}</p>
    </div>
  );
};

const ItemEditModal: React.FC<Props> = ({
  sessionId,
  item,
  onClose,
  onSaved,
  initialTab,
  canEdit = true,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const isBmw = user?.brand === Brand.BMW;
  const [form, setForm] = useState<SessionItemEditRequest>({
    quantity: item.quantity ?? 0,
    description: item.description ?? '',
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [history, setHistory] = useState<ItemEditHistEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Default tab: whichever is permitted and requested
  const [activeTab, setActiveTab] = useState<'edit' | 'history'>(
    canEdit ? (initialTab ?? 'edit') : 'history'
  );

  useEffect(() => {
    const load = async () => {
      try {
        const h = await getItemHistory(sessionId, item.id);
        setHistory(h);
      } catch {
        // history not critical — ignore errors
      } finally {
        setHistoryLoading(false);
      }
    };
    load();
  }, [sessionId, item.id]);

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await editItem(sessionId, item.id, form);
      onSaved(updated);
    } catch {
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            {canEdit ? 'Edit Item' : 'Item History'} — <span className="font-mono text-blue-600">{item.partNumber}</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Tabs — only render tab switcher when both tabs are available */}
        {canEdit && (
          <div className="flex border-b px-6">
            <button
              onClick={() => setActiveTab('edit')}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition ${activeTab === 'edit' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Edit
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition ${activeTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              History {history.length > 0 && <span className="ml-1 bg-blue-100 text-blue-700 rounded-full px-1.5 text-xs">{history.length}</span>}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === 'edit' && canEdit ? (
            <div className="space-y-4">
              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={form.description ?? ''}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Received Quantity</label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={form.quantity ?? 0}
                    onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value, 10) || 0 }))}
                  />
                </div>
              </div>

              {/* Read-only info */}
              <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Item info (read-only)</p>
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField label="Part Number"   value={item.partNumber} />
                {isBmw && <ReadOnlyField label="Wip No."       value={item.wipNo} />}
                <ReadOnlyField label="Expected Qty"  value={String(item.shipQtd ?? 0)} />
                <ReadOnlyField label="Reference"     value={item.reference} />
                <ReadOnlyField label="Order Number"  value={item.orderNumber} />
                <div className="col-span-2">
                  <ReadOnlyField label="Delivery Notes" value={item.deliveryNotes} />
                </div>
              </div>

              {saveError && (
                <p className="text-red-600 text-sm">{saveError}</p>
              )}
            </div>
          ) : (
            /* History Tab */
            <div>
              {historyLoading ? (
                <div className="text-center text-gray-400 py-6">Loading history…</div>
              ) : history.length === 0 ? (
                <div className="text-center text-gray-400 py-6">No edit history for this item.</div>
              ) : (
                <ol className="relative border-l border-gray-200 ml-3">
                  {history.map(entry => (
                    <li key={entry.id} className="mb-6 ml-4">
                      <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-blue-400 border-2 border-white"></div>
                      <p className="text-xs text-gray-500 mb-1">
                        {convertStrDateUTCToStrDateGB(entry.editedAt)} — <strong>{entry.editedByName}</strong>
                      </p>
                      {item.deliveryNotes && item.deliveryNotes.length > 0 && (
                        <p className="text-xs text-gray-400 mb-1">
                          <i className="fas fa-truck mr-1"></i>
                          {item.deliveryNotes.join(', ')}
                        </p>
                      )}
                      <ul className="space-y-1">
                        {entry.changes.map((c, i) => (
                          <li key={i} className="text-xs text-gray-700">
                            <span className="font-medium">{c.field}:</span>{' '}
                            <span className="line-through text-red-500">{c.oldValue || '—'}</span>{' '}
                            <span className="text-green-600">{c.newValue || '—'}</span>
                          </li>
                        ))}
                      </ul>
                      {entry.note && (
                        <p className="mt-1 text-xs text-blue-600 italic">
                          <i className="fas fa-comment-alt mr-1"></i>{entry.note}
                        </p>
                      )}
                      {entry.groupEditId && (
                        <p className="mt-1 text-xs">
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/dashboard/item-search?tab=history&expand=${entry.groupEditId}`);
                            }}
                            className="text-purple-600 hover:text-purple-800 hover:underline"
                          >
                            <i className="fas fa-layer-group mr-1"></i>
                            View in Group Edit History
                          </button>
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm"
            disabled={saving}
          >
            Close
          </button>
          {activeTab === 'edit' && canEdit && (
            <button
              onClick={handleSave}
              className="px-4 py-2 gradient-bg text-white rounded-lg hover:opacity-90 transition text-sm font-medium"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemEditModal;
